export type TodayLaneId = "todo" | "doing" | "done";
export type TodayTaskDropPlacement = "before" | "after";
export type TodayLifecycleDropCommand = "start" | "complete" | "reopen" | "restore";

export type TodayBoardTaskState =
  | "planned"
  | "ready"
  | "in_progress"
  | "blocked"
  | "done"
  | "verified"
  | "needs_changes"
  | "deferred"
  | "skipped"
  | "cancelled";

export function todayLaneForState(state: TodayBoardTaskState): TodayLaneId {
  if (["planned", "ready", "deferred", "needs_changes"].includes(state)) return "todo";
  if (state === "in_progress" || state === "blocked") return "doing";
  return "done";
}

export function todayLifecycleCommandForDrop(
  sourceState: TodayBoardTaskState,
  targetLane: TodayLaneId,
): TodayLifecycleDropCommand | null {
  const sourceLane = todayLaneForState(sourceState);
  if (sourceLane === "todo" && targetLane === "doing") return "start";
  if (sourceState === "in_progress" && targetLane === "todo") return "restore";
  if (sourceLane === "doing" && targetLane === "done") return "complete";
  if (sourceState === "done" && targetLane === "doing") return "reopen";
  return null;
}

export function reorderTodayTaskIdsForDrop(
  currentIds: readonly string[],
  sourceId: string,
  targetId: string,
  placement: TodayTaskDropPlacement,
): string[] | null {
  if (sourceId === targetId) return null;
  const sourceIndex = currentIds.indexOf(sourceId);
  const targetIndex = currentIds.indexOf(targetId);
  if (sourceIndex < 0 || targetIndex < 0) return null;

  const next = currentIds.filter((id) => id !== sourceId);
  const targetIndexAfterRemoval = next.indexOf(targetId);
  if (targetIndexAfterRemoval < 0) return null;
  next.splice(targetIndexAfterRemoval + (placement === "after" ? 1 : 0), 0, sourceId);
  return next.every((id, index) => id === currentIds[index]) ? null : next;
}

export function reorderTodayTaskIdsWithinLaneForDrop(
  current: readonly { taskId: string; state: TodayBoardTaskState }[],
  sourceId: string,
  targetId: string,
  placement: TodayTaskDropPlacement,
): string[] | null {
  const source = current.find((item) => item.taskId === sourceId);
  const target = current.find((item) => item.taskId === targetId);
  if (
    source === undefined ||
    target === undefined ||
    todayLaneForState(source.state) !== todayLaneForState(target.state)
  ) {
    return null;
  }

  const lane = todayLaneForState(source.state);
  const laneIds = current
    .filter((item) => todayLaneForState(item.state) === lane)
    .map((item) => item.taskId);
  const nextLaneIds = reorderTodayTaskIdsForDrop(laneIds, sourceId, targetId, placement);
  if (nextLaneIds === null) return null;

  let laneIndex = 0;
  return current.map((item) => {
    if (todayLaneForState(item.state) !== lane) return item.taskId;
    const nextId = nextLaneIds[laneIndex];
    laneIndex += 1;
    return nextId ?? item.taskId;
  });
}
